/* eslint-disable no-magic-numbers */
import { CircleLayerSpecification } from 'mapbox-gl';

import { SymbolLayerWithMapIcon } from '../../components/MapIcon';
import { SELECTED_CATCH_POSITION_HIGHLIGHT_SIZE } from '../../constants/mapLayers/catchPositions';
import {
  WAYPOINTS_MINZOOM,
  WAYPOINTS_SIMPLIFIED_CIRCLE_RADIUS,
  WAYPOINTS_SIMPLIFIED_MINZOOM,
  WAYPOINTS_ICON_SIZE,
  WAYPOINTS,
  Waypoint,
  Waypoints,
  ALL_WAYPOINTS_TOGGLEABLE_GROUPS,
} from '../../constants/mapLayers/waypoints';
import { LayerWithToggleableGroup } from '../../types/types';
import { SOURCE } from '../sources';

/* NB. The 'source-layer' value needs to match the value set in the mapbox tileset explorer for the tilset (source) you are using */

type WaypointLayers = Record<keyof Waypoints, LayerWithToggleableGroup & SymbolLayerWithMapIcon>;
type WaypointSimplifiedLayers = Record<
  keyof Waypoints,
  LayerWithToggleableGroup & CircleLayerSpecification
>;

const generateWaypointLayer = ({
  layerId,
  typeId,
  icon,
  toggleableGroup,
}: Waypoint): LayerWithToggleableGroup & SymbolLayerWithMapIcon => ({
  id: layerId,
  type: 'symbol',
  source: SOURCE.waypoints.name,
  'source-layer': 'waypoints',
  minzoom: WAYPOINTS_MINZOOM,
  paint: { 'text-color': '#FFFFFF' },
  layout: {
    visibility: 'visible',
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'icon-image': icon,
    'icon-size': WAYPOINTS_ICON_SIZE,
  },
  filter: ['==', 'symbol', typeId],
  toggleableGroups: [toggleableGroup],
});

const generateWaypointSimplifiedLayer = ({
  background,
  layerId,
  typeId,
  toggleableGroup,
}: Waypoint): LayerWithToggleableGroup & CircleLayerSpecification => ({
  id: `${layerId}_simplified_layer`,
  type: 'circle',
  source: SOURCE.waypoints.name,
  'source-layer': 'waypoints',
  minzoom: WAYPOINTS_SIMPLIFIED_MINZOOM,
  maxzoom: WAYPOINTS_MINZOOM,
  paint: {
    'circle-color': background,
    'circle-radius': WAYPOINTS_SIMPLIFIED_CIRCLE_RADIUS,
    'circle-stroke-color': '#FFFFFF',
    'circle-stroke-width': 1,
  },
  filter: ['==', 'symbol', typeId],
  toggleableGroups: [toggleableGroup],
});

export const WAYPOINTS_HIGHLIGHT_LAYER: LayerWithToggleableGroup & SymbolLayerWithMapIcon = {
  id: 'waypoints_highlight_layer',
  type: 'symbol',
  source: SOURCE.waypoints.name,
  'source-layer': 'waypoints',
  minzoom: WAYPOINTS_MINZOOM,
  layout: {
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'icon-image': 'catch_select',
    'icon-offset': [0, 16],
    'icon-size': SELECTED_CATCH_POSITION_HIGHLIGHT_SIZE,
  },
  filter: ['==', 'id', ''],
  toggleableGroups: ALL_WAYPOINTS_TOGGLEABLE_GROUPS,
};

export const WAYPOINTS_SIMPLIFIED_HIGHLIGHT_LAYER: LayerWithToggleableGroup &
  SymbolLayerWithMapIcon = {
  id: 'waypoints_simplified_highlight_layer',
  type: 'symbol',
  source: SOURCE.waypoints.name,
  'source-layer': 'waypoints',
  minzoom: WAYPOINTS_SIMPLIFIED_MINZOOM,
  maxzoom: WAYPOINTS_MINZOOM,
  layout: {
    visibility: 'visible',
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'icon-image': 'highlight',
    'icon-size': ['step', ['zoom'], 0.7, 8, 0.7, 9, 0.7, 11, 0.8],
  },
  filter: ['==', 'id', ''],
  toggleableGroups: ALL_WAYPOINTS_TOGGLEABLE_GROUPS,
};

export const WAYPOINTS_LAYERS = Object.entries(WAYPOINTS).reduce(
  (waypointLayers, [waypointKey, waypointValue]) => ({
    ...waypointLayers,
    [waypointKey]: generateWaypointLayer(waypointValue),
  }),
  {},
) as WaypointLayers;

export const WAYPOINTS_SIMPLIFIED_LAYER = Object.entries(WAYPOINTS).reduce(
  (waypointLayers, [waypointKey, waypointValue]) => ({
    ...waypointLayers,
    [waypointKey]: generateWaypointSimplifiedLayer(waypointValue),
  }),
  {},
) as WaypointSimplifiedLayers;
